import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useChildStories } from '../../hooks/useChildStories';
import { getTodaysChallenges } from '../../api/challenges';
import challengeStarIcon from '../../assets/icons/challenge-star.webp';
import storyShipCardIcon from '../../assets/icons/story-ship-card.webp';
import dailyStoryAvailableIcon from '../../assets/icons/daily-story-available.webp';
import dailyStoryDoneIcon from '../../assets/icons/daily-story-done.webp';
import s from './MapFloatingControls.module.css';

/**
 * Mobile-only set of floating controls overlaid on the map:
 *   - top-left: Daily Story button (book when available, sleeping dragon when used)
 *   - top-right: challenge / star button (with done badge)
 *   - bottom-right: Story Ship trigger
 *
 * Each card is a 105x105 translucent rounded square (rgba(255,255,255,0.4)),
 * matching Figma node 2745:4176. Hidden at >= 1280px (LearnerSidebar takes
 * over on desktop).
 */
export default function MapFloatingControls({ childId, onOpenChallenges, onOpenStoryShip, challengeOpen }) {
  const navigate = useNavigate();
  const { data: storiesData } = useChildStories(childId);
  const dailyLimitUsed = !!storiesData?.daily_limit_used;

  // FP-CLIENT-SUBMIT-GUARD-01: immediate, single-fire feedback on Create. The
  // ref blocks a same-frame second tap; the state disables the button (reusing
  // the existing :disabled style) so the tap visibly registers. This component
  // unmounts on navigate, so the latch resets naturally.
  const navigatingRef = useRef(false);
  const [navigating, setNavigating] = useState(false);

  function handleDailyStory() {
    if (dailyLimitUsed || navigatingRef.current) return;
    navigatingRef.current = true;
    setNavigating(true);
    navigate('/story/topic-picker');
  }

  const { data: challenges } = useQuery({
    queryKey: ['challenges', childId],
    queryFn: () => getTodaysChallenges(childId),
    enabled: !!childId,
    staleTime: 30_000,
  });

  const coreDone = !!challenges?.core?.completed_at;
  const challengeCount = challenges?.core
    ? Math.max(0, challenges.core.target_count - challenges.core.progress_count)
    : null;

  return (
    <div className={s.layer} aria-hidden={false}>
      <button
        type="button"
        className={`${s.dailyStoryBtn} ${dailyLimitUsed ? s.dailyStoryDone : ''}`}
        onClick={handleDailyStory}
        disabled={dailyLimitUsed || navigating}
        aria-label={dailyLimitUsed ? "Daily story complete — see you tomorrow" : "Start today's story"}
      >
        <img
          src={dailyLimitUsed ? dailyStoryDoneIcon : dailyStoryAvailableIcon}
          alt=""
          className={s.dailyStoryIcon}
        />
      </button>

      <button
        type="button"
        className={`${s.challengeBtn} ${coreDone ? s.challengeDone : ''} ${challengeOpen ? s.challengeHidden : ''}`}
        onClick={onOpenChallenges}
        aria-label="Open today's challenges"
      >
        <img src={challengeStarIcon} alt="" className={s.challengeIcon} />
        {challengeCount != null && challengeCount > 0 && (
          <span className={s.challengeBadge}>{challengeCount}</span>
        )}
        {coreDone && (
          <span className={`${s.challengeBadge} ${s.doneBadge}`} aria-label="Done">✓</span>
        )}
      </button>

      <button
        type="button"
        className={s.storyShipBtn}
        onClick={onOpenStoryShip}
        aria-label="Open Story Ship menu"
      >
        <img
          src={storyShipCardIcon}
          alt=""
          className={s.shipIcon}
          style={{ width: 72, height: 'auto', objectFit: 'contain' }}
        />
      </button>
    </div>
  );
}
